import { apiUrl } from '../Utility/ApiUrl'
import { getElements } from '../Utility/Dom'
import { reinitOnSwap } from '../Utility/SwapReinit'

// `data-next-url` points at the full result page (needed for the address bar and
// no-JS fallback), but fetching it wholesale to scrape out a handful of cards
// pulls the entire document — including its own page-specific CSP-nonced <style>
// tags, which the browser then flags as violations against the CURRENT page's
// nonce as soon as the fetched markup is parsed. `/api/course-search/results`
// (VHW-147) already renders the same result/pagination markup as a small JSON
// fragment for exactly this kind of incremental fetch, so translate the page
// URL's Extbase-namespaced query args into that endpoint's plain ones instead.
const ENDPOINT = '/api/course-search/results'
const PLUGIN_PREFIX = 'tx_sitepackage_coursesearch'
const PREFIX_PATTERN = new RegExp(`^${PLUGIN_PREFIX}\\[(filter|q|page|sort)\\](.*)$`)

interface ResultsFragmentResponse {
  fragments?: {
    results?: string
  }
}

class LoadMore {
  private readonly containers: NodeListOf<HTMLElement>

  constructor() {
    this.containers = getElements('search-load-more')
  }

  public init(): void {
    this.containers.forEach((container: HTMLElement): void => {
      this.initContainer(container)
    })
  }

  private announce(container: HTMLElement, message: string): void {
    if (!message) return
    const announcer = this.getAnnouncer(container)
    if (!announcer) return
    // Clear first so identical consecutive messages (e.g. same page size on
    // every "load more" click) still register as a DOM mutation — otherwise
    // some screen readers won't re-announce unchanged live-region text.
    announcer.textContent = ''
    announcer.textContent = message
  }

  // Button is removed once there are no more pages — if it currently holds
  // focus, move it to the "all results loaded" announcer instead of letting
  // it fall back to <body>. Skip this during automatic page-load restoration,
  // where the button was never focused by the user.
  private finish(container: HTMLElement, btn: HTMLButtonElement): void {
    const shouldRefocus = document.activeElement === btn
    const announcer = this.getAnnouncer(container)
    btn.remove()
    if (shouldRefocus) announcer?.focus()
  }

  private formatLabel(template: string | undefined, count: number): string {
    return (template ?? '').replace('%s', String(count))
  }

  private getAnnouncer(container: HTMLElement): HTMLElement | null {
    return container.querySelector<HTMLElement>('[data-js="search-load-more-announcer"]')
  }

  private getCurrentPage(container: HTMLElement): number {
    return parseInt(container.dataset.currentPage ?? '1', 10)
  }

  private initContainer(container: HTMLElement): void {
    const btn = container.querySelector<HTMLButtonElement>('[data-js="search-load-more-btn"]')
    if (!btn) return

    btn.addEventListener('click', (): void => {
      this.loadNextPage(container, btn)
    })

    this.restoreFromUrl(container, btn)
  }

  private async loadNextPage(container: HTMLElement, btn: HTMLButtonElement): Promise<void> {
    const nextUrl = container.dataset.nextUrl
    if (!nextUrl) return

    btn.disabled = true

    try {
      const response = await fetch(this.toApiUrl(nextUrl), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('Course search request failed')

      const data = await response.json() as ResultsFragmentResponse
      const resultsHtml = data.fragments?.results
      if (typeof resultsHtml !== 'string' || resultsHtml.trim() === '') {
        throw new Error('Course search response has no results fragment')
      }

      const template = document.createElement('template')
      template.innerHTML = resultsHtml.trim()

      const newItems = template.content.querySelectorAll('.search-results__item')
      const list = document.querySelector('.search-results__list')

      if (!list || newItems.length === 0) {
        throw new Error('Course search result markup is invalid')
      }

      newItems.forEach((item) => list.appendChild(item))

      const newLoadMore = template.content.querySelector('[data-js="search-load-more"]')
      const newNextUrl = newLoadMore?.getAttribute('data-next-url')

      if (newNextUrl) {
        container.dataset.nextUrl = newNextUrl
        container.dataset.currentPage = String(this.getCurrentPage(container) + 1)
        btn.disabled = false
        this.announce(container, this.formatLabel(container.dataset.labelLoaded, newItems.length))
      } else {
        container.dataset.currentPage = container.dataset.lastPage ?? container.dataset.currentPage
        this.updateUrl(container)
        this.announce(container, container.dataset.labelDone ?? '')
        this.finish(container, btn)
        return
      }

      this.updateUrl(container)
    } catch {
      btn.disabled = false
    }
  }

  private async restoreFromUrl(container: HTMLElement, btn: HTMLButtonElement): Promise<void> {
    const url = new URL(window.location.href)
    const targetPage = parseInt(url.searchParams.get('page') ?? '0', 10)
    const currentPage = this.getCurrentPage(container)
    const lastPage = parseInt(container.dataset.lastPage ?? '1', 10)
    const clampedTarget = Math.min(targetPage, lastPage)

    if (clampedTarget <= 1 || clampedTarget <= currentPage) return

    history.scrollRestoration = 'manual'

    const pagesToLoad = clampedTarget - currentPage
    const resultsPerPage = parseInt(container.dataset.resultsPerPage || '10', 10)

    for (let i = 0; i < pagesToLoad; i++) {
      const nextUrl = container.dataset.nextUrl
      if (!nextUrl) break

      await this.loadNextPage(container, btn)
    }

    const firstIndexOnPage = (clampedTarget - 1) * resultsPerPage

    setTimeout(() => {
      const items = document.querySelectorAll('.search-results__item')
      const targetItem = items[firstIndexOnPage] as HTMLElement | undefined

      if (targetItem) {
        const top = targetItem.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ behavior: 'instant', top })
      }
    }, 100)
  }

  private toApiUrl(pageUrl: string): string {
    const source = new URL(pageUrl, window.location.origin)
    const params = new URLSearchParams()

    source.searchParams.forEach((value, key): void => {
      const match = PREFIX_PATTERN.exec(key)
      if (!match) return
      const [, group, rest] = match
      params.append(`${group}${rest}`, value)
    })

    return `${apiUrl(ENDPOINT)}?${params.toString()}`
  }

  private updateUrl(container: HTMLElement): void {
    const page = this.getCurrentPage(container)
    const url = new URL(window.location.href)

    if (page > 1) {
      url.searchParams.set('page', String(page))
    } else {
      url.searchParams.delete('page')
    }

    history.replaceState({ page }, '', url.toString())
  }
}

const loadMore = new LoadMore()
loadMore.init()
reinitOnSwap(() => new LoadMore())
